import { CentralizedLogin, Config } from '@foxcorp/foxkit-profile-js/centralized-login';
import Cookies from 'js-cookie';
import { signOut } from 'State/User';
import { getXid } from 'Api/ProfileApi';
import { mapEnvironment } from './ProfileSDK';
import { cookieManager } from './CookieManager';
import { getCookieDomain, getCookieName } from './AuthStore';
import { getAppConfig, initUserState } from 'State/store';
import { HIDE_SECOND_SCREEN_QUERY_PARAM, SHOW_SECOND_SCREEN_OPTION_PARAM } from './Constants';

//Helpers related to CentralizedLoginSDK

let centralizedLogin = {};

export const centralizedLoginSDK = () => {
  if (!Object.keys(centralizedLogin).length) {
    createCentralizedLogin();
  }

  return centralizedLogin;
};

export const createCentralizedLogin = () => {
  const { authClientId, appVersion, appEnv, accountApiKey, authRedirectUri, authUrl, identityApiBaseUrl } =
    getAppConfig().defaultConfig;

  const config = {
    apiKey: accountApiKey,
    clientId: authClientId,
    redirectUri: authRedirectUri,
    environment: mapEnvironment(appEnv),
    appName: 'fox-nation-commerce',
    isSsr: typeof window === 'undefined',
    appVersion,
    authBaseUrl: authUrl,
    baseUrl: identityApiBaseUrl ? identityApiBaseUrl : undefined,
    useEksEndPoints: false,
    statefulRefreshToken: {
      useHydraForRefresh: true,
      cookieManager: cookieManager(),
      authCookieDomain: getCookieDomain(),
      authCookieName: getCookieName(),
      onRefreshSuccess: () => {
        initUserState();
      },
      onRefreshError: (error) => {
        console.error('[CentralizedLoginSDK][core] onRefreshError', error);

        if (typeof window !== 'undefined') {
          signOut(true);
        }
      },
      use: true,
    },
  };

  const centralizedLoginConfig = new Config(config);
  centralizedLogin = new CentralizedLogin(centralizedLoginConfig);
};

const getHeapUrl = async () => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  if (!window.heap?.loaded) {
    const heapLoadPromise = new Promise((resolve) => {
      window.heapReadyCb.push({
        name: 'getHeapUrl-wait-load',
        fn: resolve,
      });
    });
    const timeoutPromise = new Promise((resolve) => {
      setTimeout(resolve, 5000);
    });

    await Promise.race([heapLoadPromise, timeoutPromise]);
  }

  if (!window.heap?.loaded) {
    return undefined;
  }

  if (window.heap) {
    const { heapId } = getAppConfig().defaultConfig;
    const envId = window.heap.appid ?? heapId ?? '';

    const userId = window.heap.getUserId() ?? '';

    const sessionId = window.heap.getSessionId() ?? '';

    const metaCookie = Cookies.get(`_hp5_meta.${envId}`);
    const sessionStartTime = JSON.parse(metaCookie ?? '{}')?.sessionProperties?.time ?? '';

    const identity = window.heap.getIdentity() ?? '';

    return [envId, userId, sessionId, sessionStartTime, identity].join(';');
  }

  return undefined;
};

export const callCentralAuthLogin = async (params) => {
  const { xid } = await getXid();

  if (!xid) {
    throw new Error('Could not get XID');
  }

  if (!centralizedLoginSDK || typeof window === 'undefined') {
    throw new Error('CentralizedLoginSDK not initialized');
  }

  const heapUrl = await getHeapUrl();

  const searchParams = new URLSearchParams(window.location ? window.location.search : '');

  const hideSecondScreenParam = searchParams.get(HIDE_SECOND_SCREEN_QUERY_PARAM) === 'true';

  await centralizedLoginSDK().login({
    deviceId: xid,
    heap5CdcUrl: heapUrl,
    ...params,
    optionalParams: {
      ...(params?.optionalParams ?? {}),
      ...(hideSecondScreenParam ? {} : { [SHOW_SECOND_SCREEN_OPTION_PARAM]: 'true' }),
    },
  });
};
